import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import BoardGrid from "../components/BoardGrid";
import SentenceTrack from "../components/SentenceTrack";
import SentenceViewButton from "../components/SentenceViewButton";
import Legend from "../components/Legend";
import { CODES, getRandomCode } from "../data/codebook";
import { getOrderedTokensForLang } from "../data/codebook-helpers";
import { scoreSolo, type SoloScoreBreakdown, type Strictness } from "../game/scoring";
import { formatSentence } from "../game/format";
import type { Lang } from "../i18n";

export default function Solo() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage as Lang) ?? "en";

  const [maxIcons, setMaxIcons] = useState<number>(8);
  const [strictness, setStrictness] = useState<Strictness>(() =>
    (localStorage.getItem("lexikon.solo.strictness") as Strictness) || "normal",
  );
  const [code, setCode] = useState(() => getRandomCode());
  const [guess, setGuess] = useState("");
  const [result, setResult] = useState<SoloScoreBreakdown | null>(null);
  const [showSolution, setShowSolution] = useState(false);

  function changeStrictness(s: Strictness) {
    setStrictness(s);
    localStorage.setItem("lexikon.solo.strictness", s);
    // Re-score the current attempt if there is one so the feedback updates live.
    if (result) setResult(scoreSolo(code, lang, guess, s));
  }

  const pool = useMemo(() => CODES.filter((c) => c.iconCount <= maxIcons), [maxIcons]);

  // Tokens reordered for the current play language so order numbers read naturally.
  const orderedTokens = useMemo(() => getOrderedTokensForLang(code, lang), [code, lang]);

  function newRound() {
    const pick = pool[Math.floor(Math.random() * pool.length)];
    setCode(pick);
    setGuess("");
    setResult(null);
    setShowSolution(false);
  }

  function check() {
    setResult(scoreSolo(code, lang, guess, strictness));
    setShowSolution(true);
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>{t("solo.title")}</h2>
        <span className="pill accent">#{code.id} · {t("solo.iconsCount", { count: code.iconCount })}</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink-muted)" }}>
            {t("solo.difficulty")}:
            <select value={maxIcons} onChange={(e) => setMaxIcons(Number(e.target.value))}
              style={{ padding: "4px 8px", borderRadius: 8, border: "1px solid var(--line)", background: "var(--bg-2)", color: "var(--ink)" }}>
              <option value={4}>{t("solo.iconsCount", { count: 4 })}</option>
              <option value={5}>≤ 5</option>
              <option value={6}>≤ 6</option>
              <option value={8}>{t("solo.diffAny")}</option>
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink-muted)" }}
                 title={t("solo.scoringHint")}>
            {t("solo.scoringMode")}:
            <select value={strictness} onChange={(e) => changeStrictness(e.target.value as Strictness)}
              style={{ padding: "4px 8px", borderRadius: 8, border: "1px solid var(--line)", background: "var(--bg-2)", color: "var(--ink)" }}>
              <option value="strict">{t("solo.scoreStrict")}</option>
              <option value="normal">{t("solo.scoreNormal")}</option>
              <option value="easy">{t("solo.scoreEasy")}</option>
            </select>
          </label>
          <button className="btn ghost" onClick={newRound}>{t("solo.newCode")}</button>
        </div>
      </div>

      <div className="composer">
        <div>
          <BoardGrid tokens={orderedTokens} showSolutionGloss={showSolution} />
        </div>

        <aside className="panel" style={{ display: "grid", gap: 16, alignContent: "start" }}>
          <Legend />
          <div>
          <h3>{t("solo.yourGuess")}</h3>
          <SentenceTrack tokens={orderedTokens} />
          <div style={{ marginTop: 8 }}>
            <SentenceViewButton tokens={orderedTokens} />
          </div>
          <textarea
            value={guess}
            onChange={(e) => setGuess(e.target.value)}
            placeholder={t("solo.yourGuessPlaceholder")}
            rows={4}
            style={{ width: "100%", marginTop: 12, padding: 10, borderRadius: 8, border: "1px solid #d6dbed" }}
            disabled={showSolution}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            {!showSolution ? (
              <button className="btn primary" onClick={check} disabled={!guess.trim()}>{t("solo.check")}</button>
            ) : (
              <button className="btn primary" onClick={newRound}>{t("solo.next")} →</button>
            )}
            <button className="btn ghost" onClick={() => setShowSolution((s) => !s)}>
              {showSolution ? t("solo.hideSolution") : t("solo.showSolution")}
            </button>
          </div>

          {result && (
            <div style={{ marginTop: 16, padding: 12, background: "var(--panel-2)", borderRadius: 8 }}>
              <div style={{ fontWeight: 800, fontSize: 18 }}>
                {t("solo.score")}: {result.total >= 0 ? "+" : ""}{result.total}
              </div>
              <div style={{ marginTop: 4 }}>
                {t("solo.iconsCorrect", { n: result.iconsCorrect, total: result.iconsTotal })}
              </div>
              <div style={{ marginTop: 4, color: result.sentenceCorrect ? "var(--ok)" : "var(--danger)" }}>
                {result.sentenceCorrect ? t("solo.fullCorrect") : t("solo.fullIncorrect")}
              </div>
            </div>
          )}

          </div>
          {showSolution && (
            <div style={{ marginTop: 16 }}>
              <h3>{t("reveal.official")}</h3>
              <p style={{ background: "var(--panel-2)", padding: 12, borderRadius: 8, fontStyle: "italic" }}>
                "{formatSentence(code.translations[lang] ?? code.translations.en)}"
              </p>
              <h3 style={{ marginTop: 16 }}>{t("solo.iconBreakdown")}</h3>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {code.glossary.map((g) => {
                  const matched = result?.perIcon.find((p) => p.iconKey === g.iconKey)?.matched;
                  return (
                    <li key={g.iconKey} style={{ display: "flex", gap: 8, padding: "4px 0", borderBottom: "1px solid #eee", alignItems: "center" }}>
                      <span style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--ink-muted)", minWidth: 36 }}>{g.iconKey}</span>
                      <span style={{ flex: 1 }}>{g.gloss[lang] ?? g.gloss.en}</span>
                      {result && <span className={`pill ${matched ? "ok" : "danger"}`}>{matched ? "✓" : "✗"}</span>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
